import { ThemeProvider } from "@/providers/ThemeProvider";
import { QueryProvider } from "@/providers/QueryProvider";
import { CookieConsentProvider } from "@/providers/CookieConsentProvider";
import { TooltipProvider } from "@/components/ui/tooltip";
import { Toaster } from "@/components/ui/sonner";
import { useAuth } from "@/providers/AuthProvider";
import { configureAuth } from "@/lib/auth-fetch";
import { setApiUrl } from "@/lib/api-config";
import { getStoredCompany } from "@/features/multi-company/utils/company-storage";
import { TourProvider } from "@/contexts/TourContext";
import { ProductTour } from "@/components/ProductTour";
import { WootricProvider } from "@/providers/WootricProvider";
import { useUserProfile } from "@/hooks/useUserProfile";
import App from "@/App";
import { useEffect } from "react";
interface AppContainerProps {
  settings?: {
    apiUrl?: string;
    clientId?: string;
    defaultUrl?: string;
    identityUrl?: string;
    redirectUri?: string;
    scope?: string;
    workspaceUrl?: string;
  };
}

export default function AppContainer({ settings }: AppContainerProps = {}): React.ReactElement {
  const { getAccessToken, instanceId } = useAuth();
  const { user: wootricUser } = useUserProfile();

  // Set apiUrl synchronously before render to ensure it's available for API calls
  if (settings?.apiUrl) {
    setApiUrl(settings.apiUrl);
  }

  useEffect(() => {
    // Read evoOrganisationId from sessionStorage (set by useAuthenticatedEmployee hook)
    const evoOrganisationId = sessionStorage.getItem('evo_organisation_id') || undefined;
    // Read stored company ID from localStorage for active company selection
    const companyId = getStoredCompany() || undefined;
    configureAuth({ getAccessToken, instanceId, evoOrganisationId, companyId });
  }, [getAccessToken, instanceId]);

  return (
    <QueryProvider>
      <CookieConsentProvider>
        <TooltipProvider>
          <ThemeProvider attribute="class" defaultTheme="light" disableTransitionOnChange>
            <TourProvider>
              <WootricProvider user={wootricUser}>
                <Toaster richColors duration={5000} closeButton />
                <ProductTour />
                <App />
              </WootricProvider>
            </TourProvider>
          </ThemeProvider>
        </TooltipProvider>
      </CookieConsentProvider>
    </QueryProvider>
  );
}
